import { TextField, MenuItem, CircularProgress, InputAdornment } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import * as yup from 'yup';
import CustomModal from './CustomModal';
import AdminButton from '../AdminButton';
import useManualBooking from '../../api/useManualBooking';
import useGetAvailableSupplies from '../../api/useGetAvailableSupplies';
import { useSnackbar } from '../../../../contexts/SnackBarContext';
import { useStaff } from '../../StaffContext';
import { isValidPhoneNumber } from '../../../../utils/phoneUtils';

const NO_SUPPLIES = [];
const NO_ERRORS = {};

const isEmail = (value) => yup.string().email().isValidSync(value);

/**
 * What is wrong with the form, by field, in the registration's own words
 * (features/auth/validation.js). The name is required; the email address and the phone number are the
 * guest's to give or not, and are checked only when filled in (docs/MANUAL_BOOKING_CONTACT.md).
 */
const validate = ({ guestName, guestEmail, guestPhoneNumber }) => {
  const errors = {};
  if (!guestName.trim()) errors.guestName = 'A név megadása kötelező!';
  if (guestEmail.trim() && !isEmail(guestEmail.trim())) errors.guestEmail = 'Az email cím helytelen!';
  if (guestPhoneNumber.trim() && !isValidPhoneNumber(guestPhoneNumber)) {
    errors.guestPhoneNumber = 'A telefonszám helytelen! Például: +36 30 123 4567';
  }
  return errors;
};

const ManualBookingModal = (props) => {
  const { show, handleClose, modalData, reloadSignal = () => null } = props;
  const { date = null, time = null } = modalData || {};
  const { manualBooking, loading: bookingLoading } = useManualBooking();
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhoneNumber, setGuestPhoneNumber] = useState('');
  // Filled on a refused submit, and a field's message goes as soon as that field is typed in again.
  const [errors, setErrors] = useState(NO_ERRORS);
  const [supplyId, setSupplyId] = useState('');
  const lastSupplyId = useRef('');
  const showSnackbar = useSnackbar();
  // The guest goes on the calendar on screen (docs/MULTI_PROVIDER.md).
  const { shownProviderId } = useStaff();

  // isFetching, not isLoading: nothing is selectable while a request is out, even if a list is on screen.
  const { data: availableSupplies = NO_SUPPLIES, isFetching: suppliesLoading } = useGetAvailableSupplies({
    date,
    startTime: time?.startTime,
    providerId: shownProviderId,
    enabled: show,
  });

  // Once supplies load, default to last selected if still available, else first — and to nothing
  // when nothing fits, so a supply chosen for an earlier slot cannot be submitted here.
  useEffect(() => {
    if (suppliesLoading) return;
    const preferred = availableSupplies.find(s => s.id === lastSupplyId.current);
    setSupplyId(preferred?.id ?? availableSupplies[0]?.id ?? '');
  }, [suppliesLoading, availableSupplies]);

  const handleSupplyChange = (e) => {
    setSupplyId(e.target.value);
    lastSupplyId.current = e.target.value;
  };

  const changeField = (field, setValue) => (e) => {
    setValue(e.target.value);
    setErrors(({ [field]: _cleared, ...rest }) => rest);
  };

  const handleManualBooking = async () => {
    if (suppliesLoading || !supplyId || !date || !time) return;
    // Nothing is sent while a field is wrong: the message under it says what to change.
    const found = validate({ guestName, guestEmail, guestPhoneNumber });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const success = await manualBooking({
      supplyId,
      date,
      startTime: time.startTime,
      guestName,
      guestEmail,
      guestPhoneNumber,
      providerId: shownProviderId,
    });
    if (success) {
      showSnackbar('Sikeres foglalás', 'success');
      reloadSignal();
      handleClose_();
    }
  };

  const handleClose_ = () => {
    setGuestName('');
    setGuestEmail('');
    setGuestPhoneNumber('');
    setErrors(NO_ERRORS);
    handleClose();
  };

  return (
    <CustomModal open={show} onClose={handleClose_} title="Foglalás">
      <TextField
        select
        value={supplyId}
        label="Szolgáltatás"
        variant="outlined"
        fullWidth
        disabled={suppliesLoading}
        onChange={handleSupplyChange}
        InputProps={suppliesLoading ? {
          endAdornment: (
            <InputAdornment position="end">
              <CircularProgress size={18} />
            </InputAdornment>
          ),
        } : undefined}
      >
        {availableSupplies.map(supply => (
          <MenuItem key={supply.id} value={supply.id}>
            {supply.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Név"
        variant="outlined"
        fullWidth
        value={guestName}
        onChange={changeField('guestName', setGuestName)}
        error={!!errors.guestName}
        helperText={errors.guestName}
      />

      {/* What the guest gave, if anything: kept on the booking and shown in its details. The guest is
          mailed there about the booking and its deletion, with no box to tick (DECISIONS.md D42), so
          the admin is told before typing a wrong address. */}
      <TextField
        label="Email (nem kötelező)"
        type="email"
        variant="outlined"
        fullWidth
        value={guestEmail}
        onChange={changeField('guestEmail', setGuestEmail)}
        error={!!errors.guestEmail}
        helperText={errors.guestEmail || 'Erre a címre visszaigazolást küldünk a foglalásról.'}
      />

      {/* type="tel": a phone brings up its number pad. */}
      <TextField
        label="Telefonszám (nem kötelező)"
        type="tel"
        variant="outlined"
        fullWidth
        value={guestPhoneNumber}
        onChange={changeField('guestPhoneNumber', setGuestPhoneNumber)}
        error={!!errors.guestPhoneNumber}
        helperText={errors.guestPhoneNumber}
      />

      <AdminButton
        text="Foglalás"
        onClick={handleManualBooking}
        isLoading={bookingLoading}
      />
    </CustomModal>
  );
};

export default ManualBookingModal;